import { useMutation } from '@tanstack/react-query';
import { api } from '@/api';
import { queryClient } from '@/queries';
import { MultiSuggestStartBody, SuggestionReviewRequestBody, SuggestionType } from '@/api/apis/multi-suggest';
import { multiSuggestKeys } from './multi-suggest.keys';
import { operateMultiSuggestItemData } from './multi-suggest.fetcher';

type CreateSuggestionReviewVariables = SuggestionReviewRequestBody & {
  id: string;
};

type UpdateSuggestionReviewVariables = CreateSuggestionReviewVariables & {
  reviewId: string;
};

export const useStartSuggestion = () => {
  return useMutation({
    mutationFn: (data: MultiSuggestStartBody) => api.multiSuggest.startSuggestion(data),
    meta: {
      invalidates: [multiSuggestKeys.getList()],
    },
    onSuccess: async (data) => {
      const suggestion = await operateMultiSuggestItemData(data);

      queryClient.setQueryData(multiSuggestKeys.getItem(data.id), suggestion);
    },
  });
};

export const useCreateSuggestionReview = () => {
  return useMutation({
    mutationFn: ({ id, ...body }: CreateSuggestionReviewVariables) => api.multiSuggest.createSuggestionReview(id, body),
    meta: {
      invalidates: [multiSuggestKeys.getList()],
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(multiSuggestKeys.getMultiSuggestResultItem(data.id), (prev: SuggestionType) => ({
        ...prev,
        reviews: data.reviews,
      }));
      queryClient.setQueryData(multiSuggestKeys.getReviews(variables.id), data.reviews);
    },
  });
};

export const useUpdateSuggestionReview = () => {
  return useMutation({
    mutationFn: ({ id, reviewId, ...body }: UpdateSuggestionReviewVariables) =>
      api.multiSuggest.updateSuggestionReviewById(id, reviewId, body),
    meta: {
      invalidates: [multiSuggestKeys.getList()],
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(multiSuggestKeys.getMultiSuggestResultItem(data.id), (prev: SuggestionType) => ({
        ...prev,
        reviews: data.reviews,
      }));
      queryClient.setQueryData(multiSuggestKeys.getReviews(variables.id), data.reviews);
    },
  });
};
